import { Component, inject } from '@angular/core';
import { NgForm } from '@angular/forms';
import { getInitials } from '@core/util/string';
import { UserProfile } from '@models/userProfile';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BusinessUsersDispatch } from '@state/business-users/business-users.dispatch';
import { UserProfileState } from '@state/user-profile';
import { UserProfileDispatch } from '@state/user-profile/dispatch';
import { cloneDeep } from 'lodash';
import { Observable } from 'rxjs';
import { SnackbarService } from '../../../shared/services/ui/snackbar.service';
import { Select } from '@ngxs/store';
import { AuthState } from '@state/auth';
import { AuthUser } from '@state/auth/auth.models';
import { ICON } from '@core/util/icon';
import {
	BusinessAccount,
	UserBusinessesService,
} from '@services/user-businesses.service';
import { BusinessFeatureFlagSelectors, BusinessState } from '@state/business';
import { NavigationService } from '@services/nav/nav.service';
import { Logger } from '@core/logger';
import { UserInvitesService } from '@services/user-invites.service';
import { Invite } from '@models/businesses';
import { MatDialog } from '@angular/material/dialog';
import {
	ViewInviteDialogComponent,
	ViewInviteDialogData,
} from './view-invite/view-invite-dialog.component';
import { filter, first } from 'rxjs/operators';
import LogRocket from 'logrocket';
import { AuthModalComponent } from '../../email-integration/auth-modal/auth-modal.component';
import { ModalService } from '@ui/modal/modal.service';

@UntilDestroy()
@Component({
	selector: 'app-details',
	templateUrl: './details.component.html',
	standalone: false,
})
export class ProfileDetailsComponent {
	modalService = inject(ModalService);

	public profile: UserProfile;
	public password: string;
	public showChangeEmail = false;
	public placeHolderInitials = '';
	public placeHolderSignature = '';

	public oldPassword: string;
	public newPassword: string;
	public newPasswordConfirmation: string;

	protected readonly ICON = ICON;

	public businessAccounts$ = this.userBusinessesService.userBusinessesAccounts$;
	public businessInvites$ = this.userInvitesService.userInvites$;
	@Select(UserProfileState.getProfile) profile$: Observable<UserProfile>;
	@Select(AuthState.getLoggedInUser) loggedInUser$: Observable<AuthUser>;
	@Select(BusinessState.businessId) businessId$: Observable<string>;
	@Select(BusinessState.getLoaded) $businessLoaded: Observable<boolean>;
	@Select(BusinessFeatureFlagSelectors.isOutlookIntegrationEnabled)
	outlookIntegrationEnabled$: Observable<boolean>;

	constructor(
		private userProfileDispatch: UserProfileDispatch,
		private businessUserDispatch: BusinessUsersDispatch,
		private snackbarService: SnackbarService,
		private userBusinessesService: UserBusinessesService,
		private userInvitesService: UserInvitesService,
		private navigationService: NavigationService,
		private dialog: MatDialog,
	) {
		this.profile$.pipe(untilDestroyed(this)).subscribe((profile) => {
			this.profile = cloneDeep(profile);
			this.placeHolderSignature = `${this.profile.firstName} ${this.profile.lastName}`;
			this.placeHolderInitials = getInitials(this.placeHolderSignature);
		});

		this.loggedInUser$.pipe(untilDestroyed(this)).subscribe((user) => {
			this.showChangeEmail = user?.providerIds.some(
				(providerId) => providerId === 'password',
			);
		});
	}

	updateProfile(form: NgForm) {
		if (form.valid) {
			this.userProfileDispatch.UpdateProfile(this.profile);
		}
	}

	updateEmail(form: NgForm) {
		if (form.valid) {
			this.userProfileDispatch.ChangeEmail(this.profile.email, this.password);
		}
	}

	saveSignature(signature: string) {
		this.businessUserDispatch.SetSignature(signature);
		this.snackbarService.openSnackBar({
			text: 'Signature saved',
			type: 'success',
		});
	}

	changePassword(form: NgForm) {
		if (form.valid) {
			this.userProfileDispatch.ChangePassword(
				this.oldPassword,
				this.newPassword,
			);
		}
	}

	createNewBusiness() {
		this.navigationService.goToCreateUser();
	}

	trackByBusinessId(_: number, businessAccount: BusinessAccount) {
		return businessAccount.businessId;
	}

	goToAccount(businessId: string) {
		Logger.info('goToAccount', businessId);
		this.userProfileDispatch.SetCurrentBusinessId(businessId);

		// Once business has started loading, navigate to dashboard
		this.$businessLoaded
			.pipe(
				filter((loaded) => !loaded),
				first(),
			)
			.subscribe(() => {
				this.navigationService.goToDashboard();
			});
	}

	openInviteModal(invite: Invite): void {
		this.dialog.open(ViewInviteDialogComponent, {
			data: {
				invite: invite,
				firstName: this.profile.firstName,
				lastName: this.profile.lastName,
			} as ViewInviteDialogData,
			height: '90vh',
			maxHeight: '700px',
			maxWidth: '900px',
			width: '90%',
		});
	}

	trackByInviteId(_: number, invite: Invite) {
		return invite.id;
	}

	connectEmail() {
		LogRocket.info('Connecting to email...');
		this.modalService.openModal(AuthModalComponent, {
			title: "Closer's Email Integration",
			noStyle: true,
		});
		// .afterClosed()
		// .pipe(take(1))
		// .subscribe(async (result) => {
		// 	if (result) {
		// 		this.connected = await this.stewartFunctions
		// 			.connected_v2()
		// 			.pipe(take(1))
		// 			.toPromise();
		// 	}
		// });
	}
}
